import { useEffect, useState } from 'react';

import { styled } from '../../../stitches.config';

import { Hypertree, HypertreeData, Node } from './Hypertree';
import { Filtered } from './components/Filtered';

const HypertreeContainer = styled('div', {
  width: '100%',
  minHeight: '500px',
});

interface HypertreeWidgetProps {
  hypertrees: Array<HypertreeData | null>;
  scifinderToken?: string;
  onChange: (selectedSyntheses: Array<Array<Record<string, Node>>>) => void;
  rrConfig: Record<string, string>;
  topContext?: (activeIdx: number) => React.ReactNode;
}

export function HypertreeWidget(props: HypertreeWidgetProps) {
  const { hypertrees, scifinderToken, onChange, rrConfig, topContext = null } = props;

  const [selectedSyntheses, setSelectedSyntheses] = useState<Record<string, Node>[][]>(() =>
    Array.from({ length: hypertrees.length }, () => [])
  );
  const [productToReactionMap, setProductToReactionMap] = useState<Record<string, number>[]>(() =>
    Array.from({ length: hypertrees.length }, () => ({}))
  );
  const [expandedNodes, setExpandedNodes] = useState<string[][]>(() =>
    Array.from({ length: hypertrees.length }, () => [])
  );

  useEffect(() => {
    setSelectedSyntheses(Array.from({ length: hypertrees.length }, () => []));
    setProductToReactionMap(Array.from({ length: hypertrees.length }, () => ({})));
    setExpandedNodes(Array.from({ length: hypertrees.length }, () => []));
  }, [hypertrees]);

  const onChangeSelection = (idx: number) => (newSelection: Record<string, Node>[]) => {
    const updatedSelection = selectedSyntheses.map((s, i) => (i === idx ? newSelection : s));
    setSelectedSyntheses(updatedSelection);
    onChange(updatedSelection);
  };

  const onChangeProductToReactionMap = (idx: number) => (newProductToReactionMap: Record<string, number>) => {
    const updatedProductToReactionMap = productToReactionMap.map((p, i) => (i === idx ? newProductToReactionMap : p));
    setProductToReactionMap(updatedProductToReactionMap);
  };

  const onChangeExpandedNodes = (idx: number) => (newExpandedNodes: string[]) => {
    const updatedExpandedNodes = expandedNodes.map((e, i) => (i === idx ? newExpandedNodes : e));
    setExpandedNodes(updatedExpandedNodes);
  };

  if (hypertrees?.length === 0) {
    return <div>No hypertree to display</div>;
  }

  if (!hypertrees) {
    return <div>Loading...</div>;
  }

  return (
    <Filtered data={hypertrees}>
      {activeIdx => {
        const hypertree = hypertrees[activeIdx];

        if (!hypertree) {
          return <div>No hypertree to display, the search may have failed to find a solution.</div>;
        }

        return (
          <>
            {topContext && topContext(activeIdx)}
            <HypertreeContainer>
              <Hypertree
                key={activeIdx}
                hypertree={hypertree}
                selectedSyntheses={selectedSyntheses[activeIdx]}
                productToReactionMap={productToReactionMap[activeIdx]}
                expandedNodes={expandedNodes[activeIdx]}
                onChangeSelection={onChangeSelection(activeIdx)}
                onChangeProductToReactionMap={onChangeProductToReactionMap(activeIdx)}
                onChangeExpandedNodes={onChangeExpandedNodes(activeIdx)}
                scifinderToken={scifinderToken}
                rrConfig={Object.entries(rrConfig).reduce<Record<string, string>>((acc, [service, baseUrl]) => {
                  if (baseUrl) {
                    acc[service] = baseUrl;
                  }
                  return acc;
                }, {})}
              />
            </HypertreeContainer>
          </>
        );
      }}
    </Filtered>
  );
}
